import React, {useCallback, useEffect, useState} from 'react';
import Box from '@mui/material/Box';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import PropTypes from 'prop-types';
import EditMultiFieldsBuilder from './Shared/EditMultiFieldsBuilder';
import RichEditorResultText from './Shared/RichEditorResultText';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';

const MultipleChoice = ({
                            index,
                            componentId,
                            fieldKey,
                            value,
                            options,
                            onChange,
                            descriptionHtml,
                            onDescriptionHtmlChange,
                            commitDescriptionHtml,
                            isEditMode,
                            enableValidation,
                            isFormArchived,
                            relatedComponent,
                            readOnly,
                            placeholderValues,
                            accessLevel,
                            error,
                            audience,
                            recipientType,
                            linkedToStudents,
                            linkedToEmployees,
                            submitMode
                        }) => {
    const [draftDescriptionHtml, setDraftDescriptionHtml] = useState(descriptionHtml || '');
    const [isEditorFocused, setIsEditorFocused] = useState(false);

    const selectedIds = Array.isArray(value) ? value : (value != null && value !== '' ? [value] : []);

    useEffect(() => {
        if (isEditorFocused) return;
        setDraftDescriptionHtml(descriptionHtml || '');
    }, [descriptionHtml, isEditorFocused]);

    const handleDraftChange = useCallback((nextHtml) => {
        setDraftDescriptionHtml(nextHtml);
        onDescriptionHtmlChange(nextHtml);
    }, [onDescriptionHtmlChange]);

    const handleFocus = useCallback(() => {
        setIsEditorFocused(true);
    }, []);

    const handleBlur = useCallback(() => {
        setIsEditorFocused(false);
        commitDescriptionHtml();
    }, [commitDescriptionHtml]);

    return (
        <Box sx={{mt: 2}}>
            {isEditMode
                ? (
                    <EditMultiFieldsBuilder
                        componentId={componentId}
                        fieldKey={fieldKey}
                        onChange={onChange}
                        enableValidation={enableValidation}
                        currentDescription={draftDescriptionHtml}
                        setCurrentDescription={handleDraftChange}
                        index={index}
                        options={options}
                        isFormArchived={isFormArchived}
                        relatedComponent={relatedComponent}
                        accessLevel={accessLevel}
                        audience={audience}
                        recipientType={recipientType}
                        linkedToStudents={linkedToStudents}
                        linkedToEmployees={linkedToEmployees}
                        placeholderValues={placeholderValues}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                    />
                )
                : (
                    <Box>
                        <RichEditorResultText
                            id={componentId}
                            description={descriptionHtml}
                            placeholderValues={placeholderValues}
                            readOnly={readOnly}
                            enableValidation={enableValidation}
                            audience={audience}
                            recipientType={recipientType}
                            linkedToStudents={linkedToStudents}
                            linkedToEmployees={linkedToEmployees}
                            submitMode={submitMode}
                        />

                    <FormControl component="fieldset" variant="standard" error={!!error}>
                        <FormGroup>
                            {options.map((option) => (
                                <FormControlLabel
                                    key={`${descriptionHtml}-option-${option.id}`}
                                    control={
                                        <Checkbox
                                            inputProps={{
                                                disabled: readOnly,
                                            }}
                                            size="small"
                                            value={selectedIds.includes(option.id)}
                                            checked={selectedIds.includes(option.id)}
                                            onChange={(e) =>
                                                onChange({
                                                    id: componentId,
                                                    optionValue: e.target.checked,
                                                    optionIndex: option.id,
                                                })
                                            }
                                        />
                                    }
                                    label={option.description}
                                />
                            ))}
                        </FormGroup>
                        <FormHelperText>{error}</FormHelperText>
                    </FormControl>
                </Box>
                )}
        </Box>
    );
};

MultipleChoice.propTypes = {
    componentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    fieldKey: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    index: PropTypes.number,
    options: PropTypes.array,
    enableValidation: PropTypes.bool,
    onChange: PropTypes.func,
    descriptionHtml: PropTypes.string,
    onDescriptionHtmlChange: PropTypes.func.isRequired,
    commitDescriptionHtml: PropTypes.func.isRequired,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    relatedComponent: PropTypes.object,
    readOnly: PropTypes.bool,
    placeholderValues: PropTypes.object,
    accessLevel: PropTypes.number,
    error: PropTypes.string,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    linkedToStudents: PropTypes.bool,
    linkedToEmployees: PropTypes.bool,
    submitMode: PropTypes.bool
};

export default MultipleChoice;
